import { MonoBehavior } from './core/monoBehavior';
import { Card } from 'components/Card';
import { CardPlace, GamePlace } from 'constants/cards';
import { SolitaireGame } from './SolitaireGame';
import { Layout } from './LayoutManager';
import { WAST_PILE_Z_INDEX } from 'constants/layout';
import { Point } from 'pixi.js';
import { GameBoardPlace } from './GameBoardPlace';

export type Move = {
    list: string[];
    from: CardPlace;
    cardToActivate?: Card;
    cardToHide?: Card;
    target: GamePlace;
};
export class WastePile extends GameBoardPlace {
    game: SolitaireGame;
    cards = [] as Card[];

    constructor(parent: SolitaireGame) {
        super(parent, 0, GamePlace.WASTE_PILE);
        this.game = parent;
        this.refresh();
    }

    getCard() {
        return this.cards.pop();
    }

    addCard(card: Card) {
        if (this.cards.length) this.cards[this.cards.length - 1].setActive(false);
        this.cards.push(card);
    }
    get length() {
        return this.cards.length;
    }
    get isEmpty() {
        return this.cards.length === 0;
    }
    reposition() {
        const wastePilePosition = Layout.getWastePilePosition(0);
        this.cards.forEach((c, i) => {
            c.setPlace({ ...c.place, position: Layout.getWastePilePosition(0), order: i });
            if (i < this.length - 3) c.setPosition(wastePilePosition);
        });

        let lastCards = [] as Card[];

        for (let index = this.cards.length - 1; index > Math.max(-1, this.cards.length - 4); index--) {
            lastCards.push(this.cards[index]);
        }
        if (!lastCards.length) return;
        lastCards.reverse();

        lastCards.forEach((c, index) => {
            c.setPlace({
                ...c.place,
                position: Layout.getWastePilePosition(index),
            });
            c.setPosition(c.place.position);
        });
    }
    get lastCard() {
        if (this.cards.length) return this.cards[this.cards.length - 1];
        return null;
    }
    refresh(duration = 0.3) {
        const wastePilePosition = Layout.getWastePilePosition(0);
        this.cards.forEach((card, i) => {
            card.setActive(false);
            card.setPlace({
                ...card.place,
                position: wastePilePosition,
                zIndex: WAST_PILE_Z_INDEX + i,
                value: this.cards.length - i,
                order: i,
            });
            if (i < this.length - 3) {
                card.moveTo(wastePilePosition, { duration });
                card.cardDisplay.castShadow(false);
            }
        });

        let lastCards = [] as Card[];

        for (let index = this.cards.length - 1; index > Math.max(-1, this.cards.length - 4); index--) {
            lastCards.push(this.cards[index]);
        }
        if (!lastCards.length) return;
        lastCards.reverse();

        lastCards.forEach((c, index) => {
            c.setPlace({
                ...c.place,
                position: Layout.getWastePilePosition(index),
            });

            if (index === lastCards.length - 1) {
                c.setActive(true);
            }
            c.cardDisplay.castShadow(true);

            c.moveTo(c.place.position, { duration });
        });
    }

    removeCard(removeCard: Card) {
        this.cards = this.cards.filter((c) => c._id !== removeCard._id);
    }
    hideInactive(addCards: number) {
        const westPilePosition = Layout.getWastePilePosition(0);
        const visibleIndex = this.cards.length - (3 - addCards);
        this.cards.forEach((c, i) => {
            c.setActive(false);
            if (i < visibleIndex) {
                c.setPlace({
                    ...c.place,
                    value: this.cards.length - i,
                    position: westPilePosition,
                    order: i,
                });
                c.moveTo(c.place.position);
                c.cardDisplay.castShadow(false);
            }
        });
    }
    getStartIndex(cardToMove: Card[]) {
        if (this.length === 0) return 0;
        if (cardToMove.length === 1) {
            if (this.length + cardToMove.length - 1 < 3) {
                return this.length + cardToMove.length - 1;
            }
            return 2;
        }
        if (cardToMove.length === 3) return 0;
        if (cardToMove.length === 2) return 1;
        return 0;
    }
    resize(): void {
        this.reposition();
    }
    reset() {
        this.cards = [] as Card[];
    }
    getUndoPosition(value: number, newLength: number): Point {
        return Layout.getWastePilePosition(0);
    }
    getNextCardPosition() {
        return Layout.getWastePilePosition(Math.min(2, this.cards.length));
    }
}
